import { useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { motion } from 'framer-motion';
import { Award, BookOpen, Scissors, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { HeroBackdrop } from '@/components/landing/immersive/HeroBackdrop';
import { HeroHeadline } from '@/components/landing/immersive/HeroHeadline';
import { HeroScreens } from '@/components/landing/immersive/HeroScreens';
import type { HeroHandoff } from '@/components/landing/immersive/heroLayout';
import { HeroScrollCue } from '@/components/landing/immersive/HeroScrollCue';
import DirectorWorkspace from '@/components/landing/DirectorWorkspace';
import DirectorStudioShell from '@/components/landing/director-studio/DirectorStudioShell';
import { WhyCorridoSection } from '@/components/landing/WhyCorridoSection';
import { StorybookPitch } from '@/components/landing/StorybookSection';
import { ChopBoxPitch } from '@/components/landing/ChopBoxPitch';
import { ToolChooser } from '@/components/landing/ToolChooser';
import {
  ToolCompass, getTool, isConsoleTool, isToolId, toolIndex, type ConsoleToolId, type ToolId,
} from '@/components/landing/ToolCompass';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { editorLang, editorPath } from '@/components/editorPages/routes';
import { useAuth } from '@/contexts/useAuth';
import { GoogleSignInButton } from '@/components/auth/GoogleSignInButton';
import { mergeFunnelContext, trackEvent } from '@/lib/analytics';
import { getGsap, hasFinePointer, isSmallScreen, prefersReducedMotion } from '@/lib/motion';
import { canAnimate, glueDashes } from '@/components/landing/immersive/heroEnv';
import { hasActiveRun } from '@/lib/active-run';
// Re-exported for the tests and callers that have always imported it from here.
export { hasActiveRun };

// Floating background decoration, PER TOOL (user directive 2026-07-22):
// corrido = the studio's own curated library shots (QC'd — the a2e-watermarked
// and phone-photo frames were screened out); story/ads/rap = none yet, the
// themed gradient orbs carry the mood (they follow --primary automatically).
// Ads stays empty on purpose: no fabricated proof media for that tool.
// welcome/storybook = none either (2026-09-24): the welcome stop is neutral
// ground for every tool, and corrido photos have no place behind a kids' book.
const TOOL_DECORATIONS: Record<ToolId, string[]> = {
  welcome: [],
  corrido: ['/corrido_bg/bg1.jpg', '/corrido_bg/bg2.jpg', '/corrido_bg/bg3.jpg', '/corrido_bg/bg4.jpg',
            '/corrido_bg/bg5.jpg', '/corrido_bg/bg6.jpg', '/corrido_bg/bg7.jpg', '/corrido_bg/bg8.jpg'],
  story: [],
  ads: [],
  storybook: [],
  chopbox: [],
  rap: [],
};

// AI Photobook + Social Profile Pic Studio hero blocks removed: nothing
// powers them since the A2E retirement, so the landing page no longer
// advertises them. Restore only when a local ComfyUI workflow backs them.

// WHERE THE PAGE OPENS (2026-09-24). A first visit lands on the welcome stop,
// which lets the visitor pick a tool. Links that expect a particular tool
// still get it: ?tool=<stop> names it outright; completion emails, /corrido
// and the gallery's resume link all point at the corrido console
// (#director-console, ?resume_corrido=); the old storybook section anchor
// (#storybook) opens the storybook stop; and a render still running from an
// earlier visit reopens the corrido console so it can reconnect after a
// refresh. The pick is made once, on load, and is never written back into the
// URL: Index scrolls on every location change and TikTok's pixel counts each
// one as a page view.
const CORRIDO_HASHES = new Set(['#director-console', '#why-corrido', '#compare']);

export function pickInitialTool(search: string, hash: string): ToolId {
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(search || '');
  } catch {
    params = new URLSearchParams();
  }
  const asked = (params.get('tool') || '').trim().toLowerCase();
  if (isToolId(asked)) return asked;
  // resume_corrido_job: the board's live-build card — reopen that build's console.
  if (params.has('resume_corrido') || params.has('resume_corrido_job')) return 'corrido';
  const anchor = (hash || '').toLowerCase();
  if (CORRIDO_HASHES.has(anchor)) return 'corrido';
  if (anchor === '#storybook') return 'storybook';
  if (anchor === '#chopbox') return 'chopbox';
  if (hasActiveRun()) return 'corrido';
  return 'welcome';
}

type SelectSource = 'compass' | 'welcome_card';

// STUDIO PAGES (Studio Blueprint 02, 2026-10-16): /music, /ads and /tiktok
// each render this section locked to one console stop (`fixedTool`): no
// compass, no welcome stage, the studio's own promise as the headline, and the
// same pitch, steps and Director console the stop has always had. Home is a
// directory of doors now (pages/Index.tsx) and never renders the console.
const STUDIO_KEY: Record<ConsoleToolId, string> = { corrido: 'music', ads: 'ads', story: 'story', rap: 'music' };

export function HeroSection({ onToolChange, fixedTool }: { onToolChange?: (tool: ToolId) => void; fixedTool?: ConsoleToolId } = {}) {
  const { t, i18n } = useTranslation();
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  // THE COMPASS: which tool this entire page currently IS. Swapping it
  // transforms the pitch, accents, and background in place — same layout,
  // same rules, no reload. Slide direction follows the compass direction.
  const [tool, setTool] = useState<ToolId>(() => fixedTool ?? pickInitialTool(location.search, location.hash));
  const [slideDir, setSlideDir] = useState(1);
  // The pitch slides in only when the stop CHANGES (2026-09-25): on arrival
  // the welcome peek, the only scroll cue on a short iPhone, used to fade in
  // and slide 28px sideways while the rest of the hero stood still.
  const [switched, setSwitched] = useState(false);
  // The Director console mounts the first time a console tool (corrido /
  // story / ads / rap) is on screen and then STAYS mounted for the rest of the
  // visit: on the welcome and storybook stops it is only hidden, so a render
  // in flight keeps polling and is still there when you swipe back. It is
  // always handed the last console tool, never undefined — undefined puts the
  // console on its old in-chat picker, and it would read the next real switch
  // as its first load.
  const [consoleTool, setConsoleTool] = useState<ConsoleToolId | null>(() => (isConsoleTool(tool) ? tool : null));
  const onConsoleStop = isConsoleTool(tool);
  const activeTool = getTool(tool);
  // Index shows the "how it works" story below this section on the welcome
  // stop only, so it hears about every stop change.
  useEffect(() => { onToolChange?.(tool); }, [tool, onToolChange]);

  const compassRef = useRef<HTMLDivElement>(null);
  const scrollToCompass = useRef(false);
  // Opened on /#director-console: the console starts in place instead of
  // fading up 30px, so the page's hash scroll (Index) measures where it ends.
  const consoleStartsInPlace = useRef((location.hash || '').toLowerCase() === '#director-console');

  const switchTool = (requested: ToolId, source: SelectSource) => {
    const next: ToolId = isToolId(requested) ? requested : 'welcome';
    if (fixedTool || next === tool) return;
    setSlideDir(toolIndex(next) > toolIndex(tool) ? 1 : -1);
    setSwitched(true);
    setTool(next);
    if (isConsoleTool(next)) setConsoleTool(next);
    trackEvent('landing_tool_selected', { tool: next, source });
  };

  // A welcome card replaces the cards with that tool's page, so bring the top
  // of it back into view: only when the compass has scrolled up behind the
  // fixed navbar or off the screen, and only after a click (the compass itself
  // never scrolls the page).
  const chooseFromCard = (next: ToolId | 'quick') => {
    // The ChopBox card opens the editor itself (its own page), not a stop.
    if (next === 'chopbox') {
      navigate(editorPath('hub', editorLang(i18n.language)));
      return;
    }
    // The quick tools card opens their page (2026-10-16).
    if (next === 'quick') {
      navigate('/tools');
      return;
    }
    scrollToCompass.current = true;
    switchTool(next, 'welcome_card');
  };
  // THE LANDING AFTER A PICK (round 3, 2026-09-25). This used to be one
  // smooth scroll started while the new stop was still settling, and the
  // ScrollTrigger refresh that follows (How it works leaving the page)
  // restores the scroll position it recorded, which cancels a smooth scroll
  // halfway: 4 of 6 story picks, and 1 of 3 storybook picks on a phone,
  // ended with the compass behind the navbar. The switch happens under the
  // pick's opaque cover, so the page now jumps before paint (nobody sees it)
  // and re-checks while the stop settles: after two frames, after each
  // ScrollTrigger refresh and on a few timers. A re-check only moves the
  // page if the compass is under the navbar again, and stops for good once
  // the visitor scrolls, taps or types. With no cover (reduced motion,
  // jsdom) the one scroll follows the visitor's motion setting.
  useLayoutEffect(() => {
    if (!scrollToCompass.current) return undefined;
    scrollToCompass.current = false;
    if (typeof window === 'undefined') return undefined;
    const covered = !!document.querySelector('[data-bento-wipe]');
    const settle = (behavior: ScrollBehavior) => {
      const el = compassRef.current;
      if (!el) return false;
      const navBottom = document.querySelector('[data-site-navbar]')?.getBoundingClientRect().bottom ?? 64;
      const top = el.getBoundingClientRect().top;
      if (top >= navBottom) return false;
      window.scrollTo({ top: Math.max(0, window.scrollY + top - navBottom - 12), behavior });
      return true;
    };
    const first: ScrollBehavior = covered || prefersReducedMotion() ? 'instant' : 'smooth';
    if (!settle(first) || !covered) return undefined;

    let alive = true;
    const stop = () => { alive = false; };
    const check = () => { if (alive) settle('instant'); };
    const intents = ['wheel', 'touchstart', 'pointerdown', 'keydown'] as const;
    intents.forEach((type) => window.addEventListener(type, stop, { passive: true, once: true }));
    let raf = requestAnimationFrame(() => { raf = requestAnimationFrame(check); });
    const timers = [350, 900, 1600, 2600].map((ms) => window.setTimeout(check, ms));
    const trigger = canAnimate() ? getGsap().ScrollTrigger : null;
    trigger?.addEventListener('refresh', check);
    return () => {
      alive = false;
      cancelAnimationFrame(raf);
      timers.forEach((id) => window.clearTimeout(id));
      trigger?.removeEventListener('refresh', check);
      intents.forEach((type) => window.removeEventListener(type, stop));
    };
  }, [tool]);

  // shadcn HSL overrides re-theme every hsl(var(--primary)) consumer inside
  // the section — orbs, chips, buttons — in one place.
  const themeVars = {
    ...(activeTool.primaryHsl ? { '--primary': activeTool.primaryHsl } : {}),
    ...(activeTool.accentHsl ? { '--accent': activeTool.accentHsl } : {}),
    ...(activeTool.gradient ? { '--gradient-primary': activeTool.gradient } : {}),
  } as CSSProperties;
  const decorationImages = TOOL_DECORATIONS[tool] ?? [];
  // The studio headline stays put while you swipe between corrido, story, ads
  // and rap (as it always has); welcome and storybook swap in their own.
  const headline = tool === 'welcome' || tool === 'storybook' || tool === 'chopbox' ? tool : 'studio';

  const markSignupIntent = (source: string) => {
    mergeFunnelContext({
      funnelSource: source,
      funnelEntryPage: 'landing',
      funnelOfferType: 'free_preview',
      funnelOfferName: 'Free Preview',
      funnelBillingModel: 'free',
    });
  };

  // THE IMMERSIVE STAGE (2026-09-24). On the welcome stop the top of the page
  // is a ~90svh stage: WebGL liquid light + floating screens of real engine
  // output behind a kinetic headline, with the top of the tool cards peeking
  // above the fold. Every other stop gets a compact stage (dim field, no
  // screens) so its pitch and the Director console are as close as before.
  const isWelcome = tool === 'welcome';
  const stageRef = useRef<HTMLDivElement>(null);
  const screensRef = useRef<HTMLDivElement>(null);
  const headlineRef = useRef<HTMLDivElement>(null);
  const pitchRef = useRef<HTMLDivElement>(null);
  const [handoff, setHandoff] = useState<HeroHandoff>('off');
  const onHandoffChange = useCallback((state: HeroHandoff) => setHandoff(state), []);

  // PHONES: the welcome stage ends where the top of the first tool card still
  // peeks ~28px above the fold (hero.css: min-height 100svh - --hero-peek),
  // and the screens band takes whatever the words leave. The gap below the
  // stage (the chooser's margin plus its header) is measured, not guessed:
  // it changes with the language and with the bento's own header. Measured
  // synchronously (before paint, and in the observer, which runs after
  // layout): a rAF here waited seconds on a phone busy compiling shaders.
  useLayoutEffect(() => {
    if (!isWelcome) return undefined;
    const stage = stageRef.current;
    const pitch = pitchRef.current;
    if (!stage || !pitch || typeof window === 'undefined') return undefined;
    let alive = true;
    const measure = () => {
      const grid = pitch.querySelector('[data-testid="tool-chooser"] ul');
      if (!alive || !grid) return;
      const gap = grid.getBoundingClientRect().top - stage.getBoundingClientRect().bottom;
      if (!(gap > 0 && gap < 400)) return;
      stage.style.setProperty('--hero-peek', `${Math.round(gap + 28)}px`);
    };
    const header = pitch.querySelector('[data-testid="tool-chooser"] header');
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (header) ro?.observe(header);
    measure();
    void (document.fonts?.ready ?? Promise.resolve()).then(measure);
    return () => {
      alive = false;
      ro?.disconnect();
      stage.style.removeProperty('--hero-peek');
    };
  }, [isWelcome]);

  const scrollToPitch = () => {
    const el = pitchRef.current;
    if (!el || typeof window === 'undefined') return;
    const navBottom = document.querySelector('[data-site-navbar]')?.getBoundingClientRect().bottom ?? 64;
    window.scrollTo({
      top: Math.max(0, window.scrollY + el.getBoundingClientRect().top - navBottom - 8),
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
  };

  // Same strings as before, per headline. Each headline has its own length,
  // so its own size ramp: the welcome line must stay on one line at 320px,
  // and it is capped by height too (11vh), so a phone held sideways shows
  // the headline AND the subtitle instead of one giant line.
  const studioKey = fixedTool ? STUDIO_KEY[fixedTool] : null;
  const copy = studioKey
    ? {
        badge: t(`studios.${studioKey}.name`), icon: <Award />,
        line1: t(`studios.${studioKey}.h1a`), line2: t(`studios.${studioKey}.h1b`), subtitle: t(`studios.${studioKey}.lead`),
        titleClass: 'text-[clamp(1.7rem,7.4vw,3.6rem)]',
        subtitleClass: '',
      }
    : headline === 'welcome'
    ? {
        badge: t('hero.welcome.badge'), icon: <Sparkles />,
        line1: t('hero.welcome.title'), line2: t('hero.welcome.titleGradient'), subtitle: t('hero.welcome.subtitle'),
        titleClass: 'text-[clamp(1.75rem,min(9.2vw,11vh),5.25rem)] lg:text-[min(5rem,5.6vw)]',
        subtitleClass: '',
      }
    : headline === 'storybook'
      ? {
          badge: t('storybook.eyebrow'), icon: <BookOpen />,
          line1: t('storybook.titleStart'), line2: t('storybook.titleHighlight'), subtitle: t('storybook.subtitle'),
          titleClass: 'text-[clamp(1.8rem,7.6vw,3.8rem)]',
          subtitleClass: '',
        }
      : headline === 'chopbox'
      ? {
          badge: t('chopboxPitch.eyebrow'), icon: <Scissors />,
          line1: t('chopboxPitch.titleStart'), line2: t('chopboxPitch.titleHighlight'), subtitle: t('chopboxPitch.subtitle'),
          titleClass: 'text-[clamp(1.8rem,7.6vw,3.8rem)]',
          subtitleClass: '',
        }
      : {
          // Studio stops on a phone (2026-09-24 review): a smaller studio
          // line, so the tool's own eyebrow and pitch headline still reach
          // the first screen instead of a second same-shaped headline
          // stacked under this one. Round 3 (2026-09-25): under 640px the
          // line is a left-aligned kicker (HeroHeadline `kicker`), sized so
          // each sentence holds one line at 360 (at 7vw the Spanish broke
          // 'Recibe un / video terminado.'). Round 5b (2026-09-25): the
          // owner's subtitle is shown on phones again, compact and
          // left-aligned under the kicker; hiding it below 640px dropped a
          // whole sentence of his copy. The pitch moves down ~5 lines.
          badge: t('hero.badge'), icon: <Award />,
          line1: t('hero.title'), line2: t('hero.titleGradient'), subtitle: t('hero.subtitle'),
          titleClass: 'text-[1.35rem] max-[359px]:text-[1.25rem] sm:text-[clamp(1.9rem,8.4vw,4.4rem)]',
          subtitleClass: 'max-sm:text-left max-sm:leading-snug max-sm:text-white/65',
        };

  return (
    // data-scroll-signature: the stop is not in the URL and a remount reopens
    // on welcome, so Back / a reload put a saved scroll back only onto the
    // same stop (ScrollManager, lib/scroll-memory.ts).
    <section className="relative overflow-hidden pb-16" style={themeVars} data-scroll-signature={`landing:${tool}`}>
      <style>{`@keyframes ldc-tool-in{from{opacity:0;transform:translateX(var(--tool-dx,24px))}to{opacity:1;transform:translateX(0)}}`}</style>

      <div
        ref={stageRef}
        data-testid="hero-stage"
        className={`hero-stage relative isolate flex flex-col${isWelcome ? ' hero-stage--full' : ''}`}
      >
        <HeroBackdrop
          immersive={isWelcome}
          primaryHsl={activeTool.primaryHsl}
          accentHsl={activeTool.accentHsl}
          stageRef={stageRef}
          screensRef={screensRef}
          calmRef={headlineRef}
          onHandoffChange={onHandoffChange}
        />
        {/* Corrido's own library shots float in the stage's outer gutters,
            xl+ only (2026-09-24 review): placed by % of the whole section
            they landed behind the pitch copy on a phone and behind the
            eyebrow on a laptop. */}
        {decorationImages.length > 0 && (
          <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-[5] hidden overflow-hidden xl:block">
            {decorationImages.slice(0, 8).map((img, i) => {
              const row = Math.floor(i / 2);
              const inset = `${row % 2 === 0 ? 2 : 4.5}%`;
              return (
                <motion.img
                  key={img}
                  src={img}
                  alt=""
                  className="absolute h-20 w-20 rounded-xl object-cover opacity-20"
                  style={{ top: `${14 + row * 21}%`, ...(i % 2 === 0 ? { left: inset } : { right: inset }) }}
                  animate={{ y: [0, -10, 0], rotate: [0, i % 2 === 0 ? 5 : -5, 0] }}
                  transition={{ duration: 4 + i, repeat: Infinity, ease: 'easeInOut', delay: i * 0.5 }}
                />
              );
            })}
          </div>
        )}
        <div className={`hero-stage__inner container mx-auto flex flex-1 flex-col px-4 pt-[4.8rem] sm:pt-24${isWelcome ? '' : ' pb-2 sm:pb-8'}`}>
          <div className={isWelcome ? 'lg:my-auto' : ''}>
            {/* THE COMPASS — swipe or tap to transform the whole page between
                stops (welcome / corrido / TikTok story / commercials & ads /
                storybook / rap). */}
            <div ref={compassRef}>
              {!fixedTool && <ToolCompass tool={tool} onChange={(next) => switchTool(next, 'compass')} />}
            </div>
            {/* Keyed on the stop AND the text: a new headline or language
                replays the reveal, and React never patches text that the
                letter split replaced. */}
            {/* A studio page's first screen is its own pitch (WhyCorridoSection
                below: eyebrow, promise, lead); the page title is for search
                engines and screen readers only, so the promise never shows twice. */}
            {studioKey ? (
              <h1 className="sr-only">{`${copy.badge}: ${copy.line1} ${copy.line2}`}</h1>
            ) : (
            <HeroHeadline
              key={`${studioKey || headline}|${copy.line1}|${copy.line2}`}
              ref={headlineRef}
              id={headline}
              badge={copy.badge}
              icon={copy.icon}
              line1={copy.line1}
              line2={copy.line2}
              subtitle={copy.subtitle}
              titleClass={copy.titleClass}
              subtitleClass={copy.subtitleClass}
              parallaxStage={isWelcome ? stageRef : undefined}
              kicker={!studioKey && headline === 'studio'}
            />
            )}
          </div>
          {isWelcome && <HeroScreens ref={screensRef} handoff={handoff} />}
          {isWelcome && <HeroScrollCue onGo={scrollToPitch} />}
        </div>
      </div>

      {/* The tablet gutter matches Features, FAQ and the footer (2026-09-25):
          px-4 put the bento and the trust row 17px from the edge at 768 and
          1024, while every other section sat 30-41px in. */}
      <div className="container relative mx-auto px-4 md:px-8 xl:px-4">
        {/* THE SALES PITCH — compass-aware: transforms in place per tool.
            A plain keyed div with a CSS slide/fade (keyed on `tool` so it
            re-mounts and re-animates on switch). AnimatePresence mode="wait"
            was replaced 2026-07-22: its exit gate could stall, leaving the old
            tool's content on screen while the theme had already switched. */}
        <div
          key={tool}
          ref={pitchRef}
          style={switched
            ? { animation: `ldc-tool-in .34s ease-out both`, ['--tool-dx' as string]: `${28 * slideDir}px` }
            : undefined}
        >
          {tool === 'welcome' && <ToolChooser onSelect={chooseFromCard} />}
          {/* id="storybook" keeps the old landing-section anchor alive:
              /#storybook opens on this stop and Index scrolls here. */}
          {tool === 'storybook' && (
            <div id="storybook" className="mt-10 scroll-mt-24">
              <StorybookPitch titleAs="h2" />
            </div>
          )}
          {/* /#chopbox opens on this stop. */}
          {tool === 'chopbox' && (
            <div id="chopbox" className="mt-10 scroll-mt-24">
              <ChopBoxPitch />
            </div>
          )}
          {onConsoleStop && <WhyCorridoSection tool={tool} />}
        </div>

        {onConsoleStop && (
          <div className="mx-auto mt-16 max-w-4xl text-center">
            {/* Just there on a phone or any touch screen (2026-09-25), like
                the pitch blocks above it: WebKit held it at opacity 0 for
                frames. The fade-up stays where a mouse is in use. */}
            <motion.div
              initial={isSmallScreen() || !hasFinePointer() ? false : { opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.5 }}
              className="mx-auto mb-10 w-full max-w-6xl space-y-6" >
              <div className="rounded-2xl border border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.07),rgba(255,255,255,0.03))] p-6 text-left shadow-[0_20px_45px_rgba(0,0,0,0.34)] ring-1 ring-white/5 sm:p-7">
                {/* Hierarchy (2026-09-24 review): heading, then semibold steps
                    with round number badges that never squeeze (shrink-0),
                    then the tips in regular muted type. */}
                {/* Balanced like the pitch headings: at 390-412 it left
                    'here' / 'aquí' alone on the last line; the dash stays
                    with 'Director' so no line opens on it. */}
                <h2 className="mb-4 text-balance text-base font-bold text-primary sm:text-lg">
                  {glueDashes(t('hero.consoleTitle'))}
                </h2>
                <ol className="space-y-3">
                  {(['hero.step1', 'hero.step2', 'hero.step3'] as const).map((key, i) => (
                    <li key={key} className="flex items-start gap-3">
                      <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/20 text-xs font-bold text-primary">{i + 1}</span>
                      <span className="text-base font-semibold text-foreground sm:text-lg">{t(key)}</span>
                    </li>
                  ))}
                </ol>
                <div className="mt-4 space-y-2 border-t border-white/[0.07] pt-4 text-[15px] leading-relaxed text-muted-foreground sm:text-base">
                  <div>{t('hero.tip1')}</div>
                  {/* The price tip names THIS stop's price: the corrido line
                      (1,000 credits) read as the price of a TikTok story or a
                      commercial when those stops showed it. Rap is coming
                      soon and has no price, so it gets no price tip
                      (2026-09-25): it fell through to corrido's. */}
                  {tool !== 'rap' && (
                    <div>
                      {t(tool === 'ads' ? 'hero.tip2Ads' : tool === 'story' ? 'hero.tip2Story' : 'hero.tip2')}
                    </div>
                  )}
                </div>
                {/* Signed-in users have no second place to go — the console below
                    IS the Director Suite now. Only signed-out visitors get a CTA. */}
                {/* A full-width, 44px tap target on a phone (round 2 review:
                    it was a 36px bar); one line beside the note from 640px,
                    where it used to squeeze onto two. */}
                {!isAuthenticated && (
                  <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-2">
                    <div className="text-[15px] font-medium text-foreground sm:text-base">{t('hero.subscribedNote')}</div>
                    <Link
                      to="/signup" onClick={() => {
                        markSignupIntent('hero_console');
                        trackEvent('signup_cta_clicked', { source: 'hero_console' });
                      }}
                      className="inline-flex min-h-[44px] w-full items-center justify-center rounded-md border border-white/10 bg-[linear-gradient(135deg,hsl(var(--primary)),hsl(var(--accent))_55%,rgb(92,104,118))] px-4 py-2.5 text-[15px] font-semibold text-primary-foreground shadow-[0_12px_26px_rgba(0,0,0,0.34)] whitespace-nowrap transition-transform hover:opacity-95 active:scale-[0.98] sm:w-auto sm:shrink-0 sm:text-sm" > {t('hero.createAccount')}
                    </Link>
                    {/* Or one tap with Google (2026-09-25): the same size as
                        the button beside it, in Google's neutral white. */}
                    <GoogleSignInButton
                      source="hero_console"
                      className="min-h-[44px] w-full rounded-md px-4 py-2.5 text-[15px] whitespace-nowrap sm:w-auto sm:shrink-0 sm:text-sm"
                    />
                  </div>
                )}
              </div>

            </motion.div>
          </div>
        )}

        {/* Director Studio — THE workspace: centered and contained (no
            transform hack, no horizontal scroll). Hidden, not unmounted, on
            the welcome and storybook stops (see consoleTool). On the page's
            max-w-6xl grid (2026-09-25), the edge the navbar, Features and the
            FAQ share: at the full container width it ran ~70px past them on
            each side at 1440. Phones and tablets are narrower than the cap. */}
        {consoleTool && (
          <div data-testid="hero-console-slot" className="mx-auto max-w-6xl" style={onConsoleStop ? undefined : { display: 'none' }}>
            <motion.div
              initial={consoleStartsInPlace.current ? false : { opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.25 }}
              className="mt-8 mb-12 text-left" >
              <DirectorStudioShell>
                <DirectorWorkspace tool={consoleTool} visible={onConsoleStop} />
              </DirectorStudioShell>
            </motion.div>
          </div>
        )}

        {/* "Good to know" (HeroTrust) is no longer drawn here (owner
            2026-10-16: "the same content just repeats"): home and every
            studio page show the same three facts in the approval rail and
            the human support line below. */}
      </div>
    </section>
  );
}
